import { makeAutoObservable } from 'mobx'
import type { IDropdownItem, IIconProps } from 'shared/ui'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { type ISmartCallFlowRingStep } from 'entities/SmartCall/api/types'
import { IntegrationKey } from 'entities/Integrations'
import { type Inbox } from 'entities/Inbox/model/Inbox'
import { FlowActionStore } from 'widgets/Flow'
import {
  generateSmartCallActionId,
  type ISmartCallActionStore,
  type ISmartCallFlowActionStoreProps,
} from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/Action'
import { RingFirstDropdownStore } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/actions/Ring/ui/RingActionEditContent/RingFirstDropdown/store/RingFirstDropdownStore'
import { type ISmartCallFlowActionType } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/actions/type'
import { UsersBlockStore } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/actions/blocks/UsersBlock'

export type IRingStoreProps = {
  id?: ISmartCallFlowActionStoreProps['id']
  parentId: ISmartCallFlowActionStoreProps['parentId']
  args?: ISmartCallFlowRingStep['args']
}

export class RingStore {
  static readonly type = 'callingFlow' satisfies ISmartCallFlowActionType
  static title = 'Ring'
  static iconProps: IIconProps = {
    icon: 'phoneRing',
    color: 'var(--action-contained-primary-normal)',
  }
  static readonly SIMULTANEOUS_MEMBERS_LIMIT = 10
  public readonly actionStore: ISmartCallActionStore

  private readonly _branchLabels: Record<'next_on_answered' | 'next_on_no_answer', string> = {
    next_on_answered: 'Call is answered',
    next_on_no_answer: 'Call is not answered',
  }

  getBranchNameByTargetId = (targetId: string): string => {
    const nextIds = this.actionStore.nextIds
    if (targetId === nextIds[0]) return this._branchLabels.next_on_answered
    if (targetId === nextIds[1]) return this._branchLabels.next_on_no_answer
    return ''
  }

  constructor(
    private _entities: IEntitiesStore,
    { id, args, ...props }: IRingStoreProps,
    private _parent?: { inbox: Inbox | null }
  ) {
    this.actionStore = new FlowActionStore({
      ...props,
      id: id || generateSmartCallActionId(`${this._stepIdType.type}_${this._stepIdType.id}`),
      type: RingStore.type,
      name: RingStore.title,
      allowedNextActions: ['callingFlow', 'playSound'],
      allowedNextActionsByBranchIndex: [
        ['aiTextingAgent'],
        [
          'callingFlow',
          'callQueue',
          'sendVoicemail',
          'playSound',
          'forwardCall',
          'sendMessage',
          'aiTextingAgent',
          'aiCallingAgent',
        ],
      ],
      edgeType: 'customEdgeWithLabel',
    })
    const ringTypeInitValue = (() => {
      if (!args?.ring_type) return undefined
      if (args.ring_type === 'crm_property' && args.crm_key && args.crm_property) {
        return `${args.crm_key}.${args.crm_property}`
      }
      const simpleRingTypes = ['simultaneous', 'round_robin', 'linear_round_robin']
      if (!simpleRingTypes.includes(args.ring_type)) {
        return `${IntegrationKey.salesmsg}.${args.ring_type}`
      }
      return args.ring_type
    })()
    this.ringFirstDropdownStore = new RingFirstDropdownStore(ringTypeInitValue)
    this.usersBlockStore = new UsersBlockStore(
      args?.auto_10_members ? undefined : args?.user_ids,
      () => this.usersLimit,
      args?.auto_10_members
    )
    this._duration = args?.duration ?? 30

    makeAutoObservable(this)
  }

  private _stepIdType: Pick<ISmartCallFlowRingStep, 'id' | 'type'> = {
    id: 'callingFlow',
    type: 'action',
  }

  get step(): ISmartCallFlowRingStep {
    const nextIds = this.actionStore.nextIds
    const value = this.ringFirstDropdownStore.value
    const isSalesmsg = value.startsWith(`${IntegrationKey.salesmsg}.`)
    const isHubspot = value.startsWith(`${IntegrationKey.hubspot}.`)
    const [, crmProperty] = value.split('.')

    return {
      ...this._stepIdType,
      args: {
        duration: this.duration,
        ring_type: isHubspot ? 'crm_property' : isSalesmsg ? crmProperty : value,
        crm_property: isHubspot ? crmProperty : null,
        crm_key: isHubspot ? IntegrationKey.hubspot : null,
        next_on_answered: nextIds[0] ?? '',
        next_on_no_answer: nextIds[1] ?? '',
        ...(this.isUsersSelect
          ? this.usersBlockStore.params
          : { user_ids: [], auto_10_members: false }),
      },
      next: nextIds[1] ?? this.actionStore.nextId ?? '',
    }
  }

  setApiError = (key: string, message: string) => {}

  validate = () => {
    if (this.isUsersSelect) {
      return this.usersBlockStore.validate()
    }
    return true
  }

  get isConfigurationError() {
    if (this.isUsersSelect) {
      return !!this.usersBlockStore.error
    }
    return false
  }

  readonly ringFirstDropdownStore: RingFirstDropdownStore
  readonly usersBlockStore: UsersBlockStore

  private _duration = 30
  get duration() {
    return this._duration
  }
  setDuration = (value: number) => {
    this._duration = value
  }
  durationItems: IDropdownItem[] = Array(11)
    .fill(0)
    .map((_, i) => {
      const seconds = 10 + i * 5
      return {
        id: seconds,
        label: `${seconds} seconds`,
      }
    })
  get durationItem(): IDropdownItem | undefined {
    return this.durationItems.find((item) => item.id === this._duration)
  }

  get usersLimit(): number | null {
    return this.ringFirstDropdownStore.value === 'simultaneous'
      ? RingStore.SIMULTANEOUS_MEMBERS_LIMIT
      : null
  }

  get isUsersSelect() {
    return (
      !this.ringFirstDropdownStore.value.includes(IntegrationKey.salesmsg) &&
      !this.ringFirstDropdownStore.value.includes(IntegrationKey.hubspot)
    )
  }

  get inboxMemberIds(): number[] {
    return this._parent?.inbox?.memberIds ?? []
  }
}
